import { z } from "zod";
import { Cik, IsoDate, Money } from "./primitives.js";
/**
 * @fileoverview Section 16 ownership forms — 3, 4 and 5 — and what they say about a
 * separation.
 *
 * An open-market purchase and an equity grant arrive on the same form in the same field;
 * this file keeps them apart, because only the first is the insider choosing to buy at a
 * price the market set.
 */
/**
 * The SEC's transaction codes, as they appear in `<transactionCode>`.
 *
 * Only the codes Mosaic distinguishes are named; any other is `other`, with the code
 * kept in `rawCode`, rather than dropped.
 */
export const TransactionCode = z.enum([
    /** Open-market or private PURCHASE. */
    "P",
    /** Open-market or private SALE. */
    "S",
    /** Grant, award or other acquisition from the issuer — compensation, not conviction. */
    "A",
    /** Disposition to the issuer: buy-backs, and the shares withheld to pay tax on a vest. */
    "D",
    /** Exercise or conversion of a derivative security. */
    "M",
    /** Payment of an exercise price or tax liability by delivering shares. */
    "F",
    /** Bona fide gift. */
    "G",
    /** A code outside the set above, preserved in `rawCode`. */
    "other",
]);
/**
 * The codes on which the insider chose to trade at a price the market set.
 *
 * A property of each transaction rather than a filter: every transaction is kept, since
 * a grant is how an incentive reset shows up.
 */
export const OPEN_MARKET_CODES = ["P", "S"];
/** Whether a transaction added shares to the owner (`A`) or removed them (`D`). */
export const AcquiredOrDisposed = z.enum(["A", "D"]);
/** One transaction from an ownership form's derivative or non-derivative table, as stated. */
export const InsiderTransaction = z.object({
    /** `true` for the derivative table — options, RSUs, warrants — `false` for shares. */
    isDerivative: z.boolean(),
    securityTitle: z.string(),
    transactionDate: IsoDate.nullable(),
    code: TransactionCode,
    /** What the filing actually wrote, so an `other` code is still investigable. */
    rawCode: z.string(),
    acquiredOrDisposed: AcquiredOrDisposed.nullable(),
    /** A decimal string, never a float; share counts can be fractional. */
    shares: z.string().regex(/^\d+(\.\d+)?$/).nullable(),
    /**
     * Null means the filing did not state a price — not that the price was zero. A grant
     * often states a price of `0`, which is kept as stated.
     */
    pricePerShare: Money.nullable(),
    /**
     * The filing's footnote saying the price is a weighted average of several fills
     * ("purchased in multiple transactions at prices ranging from ..."), shown beside the
     * price so it does not read as more precise than the filer stated.
     */
    priceFootnote: z.string().nullable(),
    sharesOwnedAfter: z.string().regex(/^\d+(\.\d+)?$/).nullable(),
    /** `D` direct, `I` indirect (held through a trust, a fund, a spouse). */
    directOrIndirect: z.enum(["D", "I"]).nullable(),
});
/** A person or entity reporting on an ownership form, with its relationship to the issuer. */
export const ReportingOwner = z.object({
    cik: Cik.nullable(),
    name: z.string(),
    isDirector: z.boolean(),
    isOfficer: z.boolean(),
    isTenPercentOwner: z.boolean(),
    isOther: z.boolean(),
    /** Present only when `isOfficer`; the filer's own words, e.g. "Chief Financial Officer". */
    officerTitle: z.string().nullable(),
});
/** A parsed Section 16 ownership form: who reported, about which issuer, and what they traded. */
export const OwnershipDocument = z.object({
    /** "3", "4", "5" — or with an amendment suffix, as the filing states it. */
    documentType: z.string(),
    periodOfReport: IsoDate.nullable(),
    issuerCik: Cik.nullable(),
    issuerName: z.string().nullable(),
    issuerTradingSymbol: z.string().nullable(),
    /**
     * Plural: a single form can be filed jointly, as a group of funds does for a shared
     * position.
     */
    owners: z.array(ReportingOwner),
    transactions: z.array(InsiderTransaction),
});
/** Did this insider choose to buy or sell at a price the market set? */
export function isOpenMarket(t) {
    return OPEN_MARKET_CODES.includes(t.code);
}
/** An open-market sale of shares; the mirror of `isOpenMarketPurchase`. */
export function isOpenMarketSale(t) {
    return t.code === "S" && !t.isDerivative;
}
/** A grant or award from the issuer: compensation, not conviction. */
export function isGrant(t) {
    return t.code === "A";
}
/**
 * An open-market purchase of shares. A `P` in the derivative table is excluded: it buys a
 * contract rather than the equity.
 */
export function isOpenMarketPurchase(t) {
    return t.code === "P" && !t.isDerivative;
}
/**
 * One transaction as the API serves it, with the filing that reported it attached. Flat
 * rather than nested under an owner, because the list is read in date order.
 */
export const InsiderActivityRow = InsiderTransaction.extend({
    accession: z.string(),
    /**
     * Position within this form's derivative or non-derivative table.
     * `(accession, isDerivative, ordinal)` is the row's identity; a key built from the
     * content is not unique, since two grants on one form can match in every field.
     */
    ordinal: z.number().int().nonnegative(),
    filingDate: IsoDate.nullable(),
    formType: z.string(),
    issuerCik: Cik.nullable(),
    issuerSymbol: z.string().nullable(),
    owner: ReportingOwner,
    /** Which side of the separation filed it, so a reader is not left matching CIKs. */
    side: z.enum(["parent", "spinco"]).nullable(),
});
/**
 * A situation's insider transactions with counts by kind, so a table that is mostly
 * grants does not read as activity. Counts, deliberately never blended into one score.
 */
export const InsiderActivity = z.object({
    items: z.array(InsiderActivityRow),
    total: z.number().int().nonnegative(),
    openMarketPurchases: z.number().int().nonnegative(),
    openMarketSales: z.number().int().nonnegative(),
    grants: z.number().int().nonnegative(),
    /**
     * How many Section 16 forms have been read for either side. Zero means none has been
     * read, which is different from insiders who have done nothing.
     */
    formsRead: z.number().int().nonnegative(),
});
//# sourceMappingURL=insider.js.map